import { SBDMainArea } from "./SBDMainArea";
import React, { useCallback, useMemo } from "react";
import { SBDContainer } from "./Layout/SBDContainer";
import { SBDBottomBar } from "./Layout/SBDBottomBar";
import { OccupationsForTrack } from "@opstel/query/models/occupationsForTrack";
import { OccupationsForTrackWithOrder, processOccupationsForTrack } from "../core/occupations";
import { DisplayConfiguration } from "@opstel/query/models/displayConfiguration";
import { useSelectionState } from "@shared/common/hooks/selectionState";
import { TrackGroup } from "@opstel/query/models/trackGroup";
import { DetailPanel } from "./SidePanel/DetailPanel";

export interface SBDProps {
  readonly occupationsForTracks: OccupationsForTrack[];
  readonly displayConfigurationByTrackCode: Record<string, DisplayConfiguration>;
  readonly trackGroups: TrackGroup[];
}

export function SBD({
  occupationsForTracks,
  displayConfigurationByTrackCode,
  trackGroups,
}: SBDProps): React.JSX.Element {
  const sortedOccupations = occupationsForTracks.toSorted(
    (a, b) =>
      (displayConfigurationByTrackCode[a.track.code]?.displayOrder ?? Number.MAX_SAFE_INTEGER) -
      (displayConfigurationByTrackCode[b.track.code]?.displayOrder ?? Number.MAX_SAFE_INTEGER),
  );

  const trackFilters = useSelectionState(
    sortedOccupations.map(({ track }) => ({
      name: track.name,
      isChecked: displayConfigurationByTrackCode[track.code]?.visibleByDefault ?? true,
      id: track.code,
    })),
  );
  const trackShouldBeVisible = useCallback(
    ({ track }: OccupationsForTrack): boolean =>
      trackFilters.items.find((filterTrack) => filterTrack.name === track.name)?.isChecked ?? true,
    [trackFilters],
  );
  const filteredOccupations: OccupationsForTrackWithOrder[] = useMemo(
    () => sortedOccupations.filter(trackShouldBeVisible).map(processOccupationsForTrack),
    [sortedOccupations, trackShouldBeVisible],
  );

  return (
    <div className="grid h-full w-full grid-cols-[auto_max-content]">
      <SBDContainer>
        <SBDMainArea
          occupationsForTracks={filteredOccupations}
          trackGroups={trackGroups}
          trackFilters={trackFilters}
        />
        <SBDBottomBar />
      </SBDContainer>
      <DetailPanel />
    </div>
  );
}
